import {X} from 'lucide-react'

interface VisionModalProps {
  isOpen: boolean
  onClose: () => void
  lang: "en" | "ja"
}

const visionContent = {
  en: {
    title: "Shikoku's Vision:",
    subtitle: "A Timeless Tale of Loyalty, Freedom, and Decentralized Wisdom",
    intro: `In the rugged mountains of Shikoku Island, where ancient forests whisper secrets of resilience and the red thread of fate binds all living things, a spirited breed was born: the Shikoku Inu. Agile hunters bred by matagi warriors, these dogs embody pure strength, unwavering loyalty, and the untamed freedom of the wild. Their story is not one of chains or masters, but of partnership and self-reliance.

Today, that same spirit lives on in the digital realm as Shikoku Inu — a community-driven token and movement that carries the essence of its namesake into the world of blockchain. This scroll reveals the vision, tools, and heart behind Shikoku Inu: a beacon for decentralization, accessible finance, and empowerment for every soul seeking independence.`,
    section1Title: "Section 1: The Vision — Decentralization & Freedom for All Life 🐾",
    section1Content: `Shikoku Inu stands for a world where no central authority holds the leash. Just as the Shikoku Inu roams the steep trails of its homeland with discipline and independence, our movement champions true decentralization — the freedom for every individual, every community, and every form of life to thrive without gatekeepers.

- No rulers, only rules of code and consensus.
- Freedom from traditional financial systems that favor the few.
- Empowerment through self-custody, where your keys mean true ownership — echoing the private key ethos that puts power back in the hands of the people.

Inspired by Japan's cultural heritage of resilience, honor, and harmony with nature, Shikoku Inu envisions a global pack where loyalty to the community fuels collective growth. Decentralization is not just technology; it is a philosophy that grants freedom for all life — from seasoned traders to curious newcomers, from humans to the broader ecosystem we all share. Together, we level up with discipline, focus, and the spirit of the hunt for a better tomorrow.

As the ancient proverb echoes through the mountains: true strength comes from within, not from collars imposed by others.`,
    section2Title: "Section 2: Shikoku Is for Everyone — Teaching & Making Investments Easy",
    section2Content: `In the spirit of the loyal Shikoku Inu companion, this movement believes crypto should not be a guarded secret for the elite. Shikoku Inu is designed to be inclusive, educational, and approachable — lowering the barriers so that anyone, anywhere, can participate confidently.

- Education as the Core Path: Through engaging content, guides, and interactive experiences (including self-custody teachings and the Shikokuments ethos), we demystify DeFi. Newcomers learn at their own pace, building knowledge without intimidation.
- Tools for All Levels: Simple swap interfaces, clear analytics, and step-by-step resources make investing straightforward. Premium features reward long-term holders, but the foundation remains open and welcoming.
- A Safe Space for Growth: Shikoku Inu creates a positive community focused on learning, mutual support, and responsible participation — turning "moonshots" into sustainable journeys rooted in discipline and understanding.

Whether you are a beginner dipping your paws into crypto or an experienced explorer seeking deeper utility, Shikoku is for everyone. No gatekeepers. No complex jargon required. Just the shared spirit of the pack: loyalty, curiosity, and the freedom to grow together.`,
    closingTitle: "The Eternal Bond — Joining the Pack",
    closingContent: `As this scroll reaches its end, remember the red thread that connects us all — the unbreakable bond of community, heritage, and shared vision. Shikoku Inu is not a fleeting trend but a living legacy: blending the timeless loyalty of the Shikoku breed with the revolutionary power of decentralized technology.

Hold with purpose. Trade with wisdom. Learn with joy.
Together, we roam the digital mountains toward greater freedom, efficiency, and prosperity for all.`
  },
  ja: {
    title: "四国犬のビジョン:",
    subtitle: "忠誠、自由、そして分散型の知恵の永遠の物語",
    intro: `四国島の険しい山々、古代の森が回復力の秘密を囁き、運命の赤い糸がすべての生命を結びつける場所で、精神に満ちた犬種が生まれました：四国犬。マタギの戦士によって育てられた敏捷な狩猟犬で、純粋な力、不屈の忠誠、そして野生の自由を体現しています。彼らの物語は鎖や主人の物語ではなく、パートナーシップと自立の物語です。

今日、その同じ精神がデジタル領域でShikoku Inuとして生き続けています—コミュニティ主導のトークンとムーブメントで、その名前の本質をブロックチェーンの世界にもたらします。この巻物はShikoku Inuのビジョン、ツール、そして心を明らかにします：分散化、アクセス可能な金融、そして独立を求めるすべての魂のための力の灯台。`,
    section1Title: "セクション1: ビジョン — すべての生命のための分散化と自由 🐾",
    section1Content: `Shikoku Inuは、中央的な権力が鎖を持たない世界を目指しています。Shikoku Inuが故郷の険しい道を規律と独立を持って歩くように、私たちのムーブメントは真の分散化を推進します—すべての個人、すべてのコミュニティ、そしてすべての生命が門番なしで繁栄する自由。

- 支配者はおらず、コードと合意のルールのみ。
- 少数に有利な伝統的な金融システムからの自由。
- 自己管理を通じたエンパワーメント、あなたの鍵が真の所有権を意味します—権力を人々の手に戻す秘密鍵の精神を反映しています。

日本の回復力、名誉、自然との調和の文化的遺産にインスパイアされ、Shikoku Inuはコミュニティへの忠誠が集合的な成長を促進するグローバルパックを想像しています。分散化は単なる技術ではありません。それはすべての生命に自由を与える哲学です—ベテラントレーダーから好奇心旺盛な初心者まで、人間から私たち全員が共有するより広いエコシステムまで。私たちは規律、集中力、そしてより良い明日を狩る精神でレベルアップします。

古代の諺が山々に響き渡ります：真の力は内から来るものであり、他者によって課された首輪からではありません。`,
    section2Title: "セクション2: Shikokuはすべての人のために — 教育と投資を簡単に",
    section2Content: `忠実なShikoku Inuの仲間の精神で、このムーブメントは暗号資産がエリートの秘密であってはならないと信じています。Shikoku Inuは包括的、教育的、そして親しみやすいように設計されています—誰でも、どこでも、自信を持って参加できるように障壁を低くします。

- 核となる道としての教育: 魅力的なコンテンツ、ガイド、そしてインタラクティブな体験（自己管理の教えやShikokumentsの精神を含む）を通じて、DeFiを謎ではなくします。初心者は自分のペースで学び、威圧感なく知識を築きます。
- あらゆるレベルのためのツール: シンプルなスワップインターフェース、明確な分析、そしてステップバイステップのリソースが投資を簡単にします。プレミアム機能は長期保有者に報酬を与えますが、基礎はオープンで歓迎されます。
- 成長のための安全な場所: Shikoku Inuは学習、相互サポート、そして責任ある参加に焦点を当てたポジティブなコミュニティを作り出します—「月に行く」を規律と理解に根ざした持続可能な旅に変えます。

暗号資産に足を踏み入れたばかりの初心者であろうと、より深いユーティリティを求める経験豊富な探検者であろうと、Shikokuはすべての人のためにあります。門番はいません。複雑な専門用語は必要ありません。ただ群れの共有精神：忠誠、好奇心、そして一緒に成長する自由。`,
    closingTitle: "永遠の絆 — 群れに加わる",
    closingContent: `この巻物が終わりに近づくとき、私たち全員を結びつける赤い糸を思い出してください—コミュニティ、遺産、そして共有ビジョンの壊れない絆。Shikoku Inuは一時的なトレンドではなく、生きた遺産です：Shikoku犬種の永遠の忠誠と分散型技術の革命的な力を融合させます。

目的を持って保有し、知恵を持って取引し、喜びを持って学びましょう。
一緒に、より大きな自由、効率、そしてすべての人のための繁栄に向かってデジタルの山々を歩きましょう。`
  }
}

export default function VisionModal({ isOpen, onClose, lang }: VisionModalProps) {
  if (!isOpen) return null

  const content = visionContent[lang]

  return (
    <div 
      className="fixed inset-0 bg-black z-[70] flex items-center justify-center p-4" 
      onClick={onClose}
    >
      <div 
        className="relative max-w-4xl w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top Wooden Rod */}
        <div className="w-full h-6 bg-gradient-to-r from-amber-900 via-amber-800 to-amber-900 rounded-t-full shadow-lg relative">
          <div className="absolute inset-0 bg-gradient-to-b from-amber-700/30 to-transparent rounded-t-full"></div>
          <div className="absolute left-1/4 top-1 w-1 h-4 bg-amber-950/30 rounded"></div>
          <div className="absolute left-2/3 top-1 w-1 h-4 bg-amber-950/30 rounded"></div>
        </div>

        {/* Scroll Paper */}
        <div 
          className="relative overflow-y-auto bg-gradient-to-b from-amber-50 via-yellow-50 to-amber-100 shadow-2xl"
          style={{ 
            backgroundImage: "url('data:image/svg+xml,%3Csvg width=\"100\" height=\"100\" xmlns=\"http://www.w3.org/2000/svg\"%3E%3Cfilter id=\"paper\"%3E%3CfeTurbulence type=\"fractalNoise\" baseFrequency=\"0.04\" numOctaves=\"5\" result=\"noise\"/%3E%3CfeDiffuseLighting in=\"noise\" lighting-color=\"%23f5e6d3\" surfaceScale=\"1\"%3E%3CfeDistantLight azimuth=\"45\" elevation=\"60\"/%3E%3C/feDiffuseLighting%3E%3C/filter%3E%3Crect width=\"100\" height=\"100\" filter=\"url(%23paper)\" opacity=\"0.8\"/%3E%3C/svg%3E')",
            boxShadow: 'inset 0 0 100px rgba(245, 222, 179, 0.5), 0 20px 50px rgba(0,0,0,0.5)'
          }}
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 bg-amber-800 text-white rounded-full flex items-center justify-center hover:bg-amber-900 transition-colors shadow-lg z-10"
            aria-label="Close modal"
          >
            <X size={24} />
          </button>

          {/* Scroll Content */}
          <div className="p-8 sm:p-12 md:p-16 space-y-8">
            {/* Title */}
            <div className="text-center border-b-4 border-amber-800 pb-6">
              <h2 className="text-4xl sm:text-5xl font-black text-amber-900 mb-3" style={{ fontFamily: "serif" }}>
                {content.title}
              </h2>
              <p className="text-2xl sm:text-3xl font-bold text-amber-800" style={{ fontFamily: "serif" }}>
                {content.subtitle}
              </p>
            </div>

            {/* Intro */}
            <div className="text-lg sm:text-xl text-amber-900 leading-relaxed whitespace-pre-line" style={{ fontFamily: "serif" }}>
              {content.intro}
            </div>

            {/* Section 1 */}
            <div className="border-t-2 border-amber-700 pt-6">
              <h3 className="text-2xl sm:text-3xl font-black text-amber-900 mb-4" style={{ fontFamily: "serif" }}>
                {content.section1Title}
              </h3>
              <div className="text-lg sm:text-xl text-amber-900 leading-relaxed whitespace-pre-line" style={{ fontFamily: "serif" }}>
                {content.section1Content}
              </div>
            </div>

            {/* Section 2 */}
            <div className="border-t-2 border-amber-700 pt-6">
              <h3 className="text-2xl sm:text-3xl font-black text-amber-900 mb-4" style={{ fontFamily: "serif" }}>
                {content.section2Title}
              </h3>
              <div className="text-lg sm:text-xl text-amber-900 leading-relaxed whitespace-pre-line" style={{ fontFamily: "serif" }}>
                {content.section2Content}
              </div>
            </div>

            {/* Closing */}
            <div className="border-t-4 border-amber-800 pt-6">
              <h3 className="text-2xl sm:text-3xl font-black text-amber-900 mb-4 text-center" style={{ fontFamily: "serif" }}>
                {content.closingTitle}
              </h3>
              <div className="text-lg sm:text-xl text-amber-900 leading-relaxed whitespace-pre-line text-center" style={{ fontFamily: "serif" }}>
                {content.closingContent}
              </div>
            </div>

            {/* Decorative Elements */}
            <div className="flex justify-center items-center gap-4 pt-6">
              <div className="w-16 h-1 bg-gradient-to-r from-transparent via-amber-800 to-transparent"></div>
              <span className="text-4xl">🐾</span>
              <div className="w-16 h-1 bg-gradient-to-r from-transparent via-amber-800 to-transparent"></div>
            </div>

            {/* Shikoku Wave Art */}
            <div className="mt-4 -mx-8 sm:-mx-12 md:-mx-16 -mb-8 sm:-mb-12 md:-mb-16">
              <img 
                src="https://static.lumi.new/a7/a73bcd5bda53e5fe278f1f6376f31179.webp" 
                alt="Shikoku Inu with waves - Japanese art style" 
                className="w-full object-cover rounded-b-sm"
              />
            </div>
          </div>
        </div>

        {/* Bottom Wooden Rod */}
        <div className="w-full h-6 bg-gradient-to-r from-amber-900 via-amber-800 to-amber-900 rounded-b-full shadow-lg relative">
          <div className="absolute inset-0 bg-gradient-to-t from-amber-700/30 to-transparent rounded-b-full"></div>
          <div className="absolute left-1/3 bottom-1 w-1 h-4 bg-amber-950/30 rounded"></div>
          <div className="absolute left-3/4 bottom-1 w-1 h-4 bg-amber-950/30 rounded"></div>
        </div>
      </div>
    </div>
  )
}
